import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Body} from 'shells/typography/body';

import type {
    GetWorkOsInformation_workOs as WorkOs,
    GetWorkOsInformation_workOs_directorySync as DirectorySync,
    GetWorkOsInformation_workOs_sso as Sso,
    GetRoles_roles as Role,
} from 'nutshell-graphql-types';

import {
    DirectorySyncConfigure,
    DirectorySyncDefaultRole,
    DirectorySyncSync,
    DirectorySyncFeatureGateModal,
} from '..';

import {
    useUpdateDirectorySyncSettings,
    useQueueDirectorySync,
    useTeardownDirectorySyncForInstance,
} from '../graphql/hooks';
import {getNonDefaultRoles, getDefaultRole, hasVerifiedDomain} from '../helpers';
import type {FormattedOption} from '../helpers';

type Props = {
    workOs?: WorkOs;
    roles: Role[];
    isLoading: boolean;
};

export function DirectorySyncSettingsSection(props: Props) {
    const {workOs} = props;

    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState<boolean>(false);
    const [defaultRole, setDefaultRole] = React.useState<FormattedOption | null | undefined>(
        workOs ? getDefaultRole(workOs) : null
    );
    const [nonDefaultRoles, setNonDefaultRoles] = React.useState<Array<FormattedOption>>(
        workOs ? getNonDefaultRoles(props.roles, workOs) : []
    );
    const [directorySync, setDirectorySync] = React.useState<DirectorySync | null>(
        workOs ? workOs.directorySync : null
    );
    const [sso, setSso] = React.useState<Sso | null>(workOs ? workOs.sso : null);
    const [canConfigureDirectorySync, setCanConfigureDirectorySync] = React.useState<boolean>(
        workOs ? Boolean(sso && sso.isConfigured && hasVerifiedDomain(workOs.domainInfo)) : false
    );

    const {updateDirectorySyncSettings} = useUpdateDirectorySyncSettings();
    const {
        queueDirectorySync,
        isLoading: isQueueDirectorySyncLoading,
        error: queueDirectorySyncError,
    } = useQueueDirectorySync();
    const {teardownDirectorySyncForInstance} = useTeardownDirectorySyncForInstance();

    // Need to update variables dependent on workOs once the component is done loading
    React.useEffect(() => {
        if (workOs) {
            setDefaultRole(getDefaultRole(workOs));
            setNonDefaultRoles(getNonDefaultRoles(props.roles, workOs));
            setDirectorySync(workOs.directorySync);
            setSso(workOs.sso);
            setCanConfigureDirectorySync(
                Boolean(
                    workOs.sso && workOs.sso.isConfigured && hasVerifiedDomain(workOs.domainInfo)
                )
            );
        }
    }, [workOs, props.roles]);

    // @ts-expect-error
    const handleDefaultRoleChange = (selectedRole) => {
        const newDefaultRole = selectedRole;
        let newNonDefaultRoles = nonDefaultRoles.filter(
            (role) => role && role.value !== selectedRole.value
        );

        if (defaultRole) {
            newNonDefaultRoles = [...newNonDefaultRoles, defaultRole].sort((a, b) => {
                if (a && b) {
                    return a.label.localeCompare(b.label);
                }

                return 0;
            });
        }

        updateDirectorySyncSettings({
            defaultRoleId: newDefaultRole.value,
        }).then(() => {
            setNonDefaultRoles(newNonDefaultRoles);
            setDefaultRole(newDefaultRole);
        });
    };

    const handleOpenAdminPortal = () => {
        if (directorySync && directorySync.adminPortal) {
            window.open(directorySync.adminPortal, '_blank');
        }
    };

    const handleDisableDirectorySync = () => {
        teardownDirectorySyncForInstance().then(() => {
            window.location.reload();
        });
    };

    const handleQueueDirectorySync = () => {
        queueDirectorySync();
    };

    if (props.isLoading) {
        return <LoadingPage />;
    }

    if (!workOs || !sso || !directorySync) {
        return (
            <Body color='error' size='small' as='div'>
                <div className='pad-16'>Error getting directory sync info</div>
            </Body>
        );
    }

    return (
        <div className='pad-16'>
            <StackLegacy spacing={16}>
                {!directorySync.isConfigured && !sso.isConfigured && directorySync.canSetup && (
                    <FlashBanner
                        type='info'
                        leftAlignContent={true}
                        hasBorder={true}
                        animate={false}
                    >
                        You will have to configure single sign-on before your directory sync.
                    </FlashBanner>
                )}
                <DirectorySyncConfigure
                    canSetup={directorySync.canSetup}
                    isConfigured={directorySync.isConfigured}
                    handleOpenAdminPortal={handleOpenAdminPortal}
                    handleDisableDirectorySync={handleDisableDirectorySync}
                    canConfigureDirectorySync={canConfigureDirectorySync}
                    setIsFeatureGateModalOpen={setIsFeatureGateModalOpen}
                />
                {sso && sso.isConfigured && directorySync.canSetup && (
                    <StackLegacy spacing={16}>
                        <DirectorySyncSync
                            directorySync={directorySync}
                            handleQueueDirectorySync={handleQueueDirectorySync}
                            isQueueDirectorySyncLoading={isQueueDirectorySyncLoading}
                            queueDirectorySyncError={queueDirectorySyncError}
                        />
                        <DirectorySyncDefaultRole
                            nonDefaultRoles={nonDefaultRoles}
                            defaultRole={defaultRole}
                            handleDefaultRoleChange={handleDefaultRoleChange}
                        />
                    </StackLegacy>
                )}
            </StackLegacy>
            {isFeatureGateModalOpen && (
                <DirectorySyncFeatureGateModal
                    onClose={() => setIsFeatureGateModalOpen(false)}
                    isOpen={isFeatureGateModalOpen}
                />
            )}
        </div>
    );
}
